import { Box, FormHelperText } from '@mui/material'
import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useLoadOptions } from 'src/hooks/useLoadOptions'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import CardActions from '../../../components/CardActions'
import {
  useLCSLHPTIConcentracionTipos,
  useLCSLHPTILimiteHoldingTipos,
  useLCSLHPTILimitePerdidaTipos,
  useLCSLHPTILimiteTipos
} from '../hooks/useLCSLHPTICache'
import {
  LCSLHPTIGroupedHeader,
  LCSLHPTIRow,
  pivotLCSLHPTIData,
  useLCSLHPTITablaValorInicial
} from '../hooks/useLCSLHPTITablaValor'
import { OperacionLCSLHPTIFields, OperacionLCSLHPTITablaValorFields } from '../types/fields'
import { FormOperacionLCSLHPTI } from '../types/types'
import LCSLHPTITablaValoresEditorGrid from './LCSLHPTITablaValoresEditorGrid'

interface SeccionNuevoLCSLHPTITablaValoresProps {
  formMethods: UseFormReturn<FormOperacionLCSLHPTI>
  codSistemaPrincipal: number
}

const SeccionNuevoLCSLHPTITablaValores = ({ formMethods, codSistemaPrincipal }: SeccionNuevoLCSLHPTITablaValoresProps) => {
  const [pivotData, setPivotData] = useState<{
    groupedHeaders: LCSLHPTIGroupedHeader[],
    rows: LCSLHPTIRow[],
  }>({ groupedHeaders: [], rows: [] });

  const { setValue, getValues, control } = formMethods;

  const { valores: tablaValorOriginal, isLoading } = useLCSLHPTITablaValorInicial(codSistemaPrincipal);

  const { limiteTipos, isLoading: ldL, isError: erL, error: eL } = useLCSLHPTILimiteTipos();
  const { limiteHoldingTipos, isLoading: ldH, isError: erH, error: eH } = useLCSLHPTILimiteHoldingTipos();
  const { concentracionTipos, isLoading: ldC, isError: erC, error: eC } = useLCSLHPTIConcentracionTipos();
  const { limitePerdidaTipos, isLoading: ldP, isError: erP, error: eP } = useLCSLHPTILimitePerdidaTipos();

  const loadLimite = useLoadOptions({ data: limiteTipos, loading: ldL, isError: erL, error: eL });
  const loadHolding = useLoadOptions({ data: limiteHoldingTipos, loading: ldH, isError: erH, error: eH });
  const loadConcentracion = useLoadOptions({ data: concentracionTipos, loading: ldC, isError: erC, error: eC });
  const loadPerdida = useLoadOptions({ data: limitePerdidaTipos, loading: ldP, isError: erP, error: eP });

  const selectoresMapping = {
    limite_credito: loadLimite,
    limite_holding: loadHolding,
    concentracion: loadConcentracion,
    limite_perdida: loadPerdida,
  };

  useEffect(() => {
    if (tablaValorOriginal.length > 0) {
      setValue(OperacionLCSLHPTIFields.TablaValores, tablaValorOriginal as any);
      const p = pivotLCSLHPTIData(tablaValorOriginal);
      setPivotData({ groupedHeaders: p.groupedHeaders, rows: p.rows });
    }
  }, [tablaValorOriginal, setValue]);

  const rellenarTablaAutomaticamente = () => {
    const valoresReferencia: Record<string, string> = {
      limite_credito: limiteTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
      limite_holding: limiteHoldingTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
      concentracion: concentracionTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
      limite_perdida: limitePerdidaTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
    };

    const nuevosValores = getValues(OperacionLCSLHPTIFields.TablaValores).map((celda: any) => ({
      ...celda,
      [OperacionLCSLHPTITablaValorFields.Valor]: valoresReferencia[celda.tipoSelector] || celda[OperacionLCSLHPTITablaValorFields.Valor]
    }));

    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });
  };

  const agregarFilaOtros = () => {
    const nuevoRowId = `temp-${Date.now()}`;
    const actuales = (getValues(OperacionLCSLHPTIFields.TablaValores) || []) as any[];

    const nuevasCeldas = pivotData.groupedHeaders.flatMap(group =>
      group.columnas.map(col => {
        const ref = actuales.find(v => v[OperacionLCSLHPTITablaValorFields.CodOperacionTipo] === col.codOperacionTipo);
        return {
          [OperacionLCSLHPTITablaValorFields.CodInstrumento]: 15,
          [OperacionLCSLHPTITablaValorFields.DescInstrumento]: "Otros",
          [OperacionLCSLHPTITablaValorFields.CodOperacionTipo]: col.codOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.DescOperacionTipo]: col.descOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.Agrupacion]: group.agrupacion,
          [OperacionLCSLHPTITablaValorFields.Valor]: "",
          [OperacionLCSLHPTITablaValorFields.Orden]: 99,
          [OperacionLCSLHPTITablaValorFields.PermiteEditarInstrumento]: true,
          [OperacionLCSLHPTITablaValorFields.TipoSelector]: ref?.[OperacionLCSLHPTITablaValorFields.TipoSelector] || "",
          rowId: nuevoRowId,
        };
      })
    );

    const nuevosValores = [...actuales, ...nuevasCeldas];
    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });

    const p = pivotLCSLHPTIData(nuevosValores);
    setPivotData({ groupedHeaders: p.groupedHeaders, rows: p.rows });
  };


  const eliminarFilaOtros = (rowKey: string | number) => {
    const actuales = getValues(OperacionLCSLHPTIFields.TablaValores) || [];
    const nuevosValores = actuales.filter((v: any) =>
      (v.rowId || v[OperacionLCSLHPTITablaValorFields.CodInstrumento]) !== rowKey
    );

    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });
    const p = pivotLCSLHPTIData(nuevosValores);
    setPivotData({ groupedHeaders: p.groupedHeaders, rows: p.rows });
  };

  return (
    <CardActions title="Matriz de Límites y Condiciones" icon='mdi:table-edit' colapsable tableContent>
      {/* <Box sx={{ px: 4, pt: 3, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="secondary" onClick={rellenarTablaAutomaticamente}>
          Rellenar valores automáticamente
        </Button>
      </Box> */}

      <LCSLHPTITablaValoresEditorGrid
        formMethods={formMethods}
        groupedHeaders={pivotData.groupedHeaders}
        rows={pivotData.rows}
        isLoading={isLoading}
        selectoresMapping={selectoresMapping}
        onAgregarOtros={agregarFilaOtros}
        onEliminarOtros={eliminarFilaOtros}
        permiteEditar={true}
      />

      {formMethods.formState.errors.tablaValores?.message && (
        <Box sx={{ px: 4, mt: 2 }}>
          <FormHelperText error>
            {formMethods.formState.errors.tablaValores.message}
          </FormHelperText>
        </Box>
      )}
    </CardActions>
  );
}

export default SeccionNuevoLCSLHPTITablaValores;